import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';

const STORAGE_KEY = 'hub_impersonation';

const ImpersonationContext = createContext({
  impersonatedUser: null,
  isImpersonating: false,
  effectiveUserId: null,
  effectiveUserName: '',
  effectiveIsAdmin: false,
  canImpersonate: false,
  setImpersonatedUser: () => {},
  clearImpersonation: () => {},
});

function readStorage() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.uid === 'string') return parsed;
    return null;
  } catch {
    return null;
  }
}

function writeStorage(user) {
  try {
    if (user) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // sessionStorage unavailable (private browsing edge cases)
  }
}

export function ImpersonationProvider({ children }) {
  const { currentUser, SuperAdmin } = useAuth();
  const canImpersonate = SuperAdmin === true || currentUser?.CRMRole === 'CRM Admin';

  const [impersonatedUser, setImpersonatedUserState] = useState(() => readStorage());

  const setImpersonatedUser = useCallback(
    (user) => {
      if (!canImpersonate) return;
      if (!user) {
        setImpersonatedUserState(null);
        writeStorage(null);
        return;
      }
      const slim = { uid: user.uid, name: user.name || '', email: user.email || '', crmRole: user.crmRole || '' };
      setImpersonatedUserState(slim);
      writeStorage(slim);
    },
    [canImpersonate]
  );

  const clearImpersonation = useCallback(() => {
    setImpersonatedUserState(null);
    writeStorage(null);
  }, []);

  const realIsAdmin = canImpersonate;

  const value = useMemo(() => {
    const isImpersonating = canImpersonate && !!impersonatedUser;
    return {
      impersonatedUser: isImpersonating ? impersonatedUser : null,
      isImpersonating,
      effectiveUserId: isImpersonating ? impersonatedUser.uid : currentUser?.uid,
      effectiveUserName: isImpersonating
        ? impersonatedUser.name
        : currentUser?.Name || currentUser?.name || '',
      effectiveIsAdmin: isImpersonating
        ? impersonatedUser.crmRole === 'CRM Admin'
        : realIsAdmin,
      canImpersonate,
      setImpersonatedUser,
      clearImpersonation,
    };
  }, [impersonatedUser, canImpersonate, realIsAdmin, currentUser, setImpersonatedUser, clearImpersonation]);

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  return useContext(ImpersonationContext);
}
